"use client";

import { getClubs } from "@/actions/getClubs";
import type { ClubFilterParams } from "@/components/discover/filters/selection";
import type { ClubSource } from "@/types/discover";
import {
  type ListPage,
  type ListViewport,
  listQueryOptions,
  usePaginatedListQuery,
} from "./paginatedListQuery";

export type { ClubFilterParams };

interface UsePaginatedClubsQueryOptions {
  filters: ClubFilterParams;
  /** Filters the server rendered `initialClubs` for. */
  serverFilters: ClubFilterParams;
  initialClubs: ClubSource[];
  initialTotal: number;
  viewport?: ListViewport;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * Generates a stable query key for paginated clubs
 */
function generateQueryKey(filters: ClubFilterParams): unknown[] {
  return [
    "paginated-clubs",
    `${filters.location.lat},${filters.location.lon}`,
    filters.activities ? filters.activities.slice().sort().join(",") : "",
    filters.ageGroups ? filters.ageGroups.slice().sort().join(",") : "",
    filters.skillLevels ? filters.skillLevels.slice().sort().join(",") : "",
    filters.membershipFee ? filters.membershipFee.slice().sort().join(",") : "",
    filters.membership?.isAcceptingSignups ?? "",
    filters.spondOnly ?? "",
    filters.searchQuery ?? "",
    filters.sort ?? "",
    filters.radiusKm ?? "",
  ];
}

type ClubsPage = ListPage<"clubs">;

/** Query key and fetcher for one paginated clubs list. */
export function clubsQueryOptions({
  filters,
  viewport,
  pageSize,
}: {
  filters: ClubFilterParams;
  viewport?: ListViewport;
  pageSize?: number;
}) {
  return listQueryOptions({
    entity: "clubs",
    radiusKey: generateQueryKey(filters),
    fetchRadiusPage: async (page) => {
      const result = await getClubs({
        location: filters.location,
        activities: filters.activities,
        membership: filters.membership,
        ageGroups: filters.ageGroups,
        skillLevels: filters.skillLevels,
        membershipFee: filters.membershipFee,
        spondOnly: filters.spondOnly ?? false,
        searchQuery: filters.searchQuery,
        sort: filters.sort,
        radiusKm: filters.radiusKm,
        ...page,
      });
      return { items: result.clubHits, total: result.total };
    },
    location: filters.location,
    viewport,
    pageSize,
  });
}

export function usePaginatedClubsQuery({
  filters,
  serverFilters,
  initialClubs,
  initialTotal,
  viewport,
  pageSize,
  enabled = true,
}: UsePaginatedClubsQueryOptions) {
  return usePaginatedListQuery({
    entity: "clubs",
    options: clubsQueryOptions({ filters, viewport, pageSize }),
    serverKey: generateQueryKey(serverFilters),
    initialItems: initialClubs,
    initialTotal,
    pageSize,
    enabled,
  });
}

/**
 * Helper to flatten paginated clubs data
 */
export function flattenClubsPages(
  pages: ClubsPage[] | undefined,
): ClubSource[] {
  if (!pages) return [];
  return pages.flatMap((page) => page.clubs);
}
